import { useState, useCallback, KeyboardEvent } from 'react'
import { Icon } from '@/components/Icon'
import { Popover } from '@/components/Popover'
import { ListItem } from '@/components/ListItem'
import { useRovingFocus } from '@/hooks/useRovingFocus'

interface SelectOption {
	value: string
	label: string
	/** Trailing text in the option row (e.g. a match count). Not shown in the trigger. */
	secondaryLabel?: string
}

type SelectFieldAlign = 'start' | 'center' | 'end'

interface SelectFieldProps {
	options: SelectOption[]
	value?: string
	onValueChange?: (value: string) => void
	placeholder?: string
	label?: string
	showCaret?: boolean
	align?: SelectFieldAlign
	spacesClass?: string
	className?: string
	containerClassName?: string
	matchTriggerWidth?: boolean
}

export function SelectField ({
	options,
	value: controlledValue,
	onValueChange,
	placeholder = 'Select…',
	label,
	showCaret = false,
	align = 'start',
	spacesClass = '',
	className = '',
	containerClassName = '',
	matchTriggerWidth = false
}: SelectFieldProps) {
	const [internalValue, setInternalValue] = useState<string | undefined>(
		undefined
	)
	const [open, setOpen] = useState(false)
	const value = controlledValue ?? internalValue

	const selectedOption = options.find(o => o.value === value)

	const handleSelect = useCallback(
		(optionValue: string) => {
			setInternalValue(optionValue)
			onValueChange?.(optionValue)
			setOpen(false)
		},
		[onValueChange]
	)

	const { listRef, onKeyDown, getItemProps } = useRovingFocus(
		options.map(o => o.value),
		value
	)

	const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
		if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
		e.preventDefault()
		setOpen(true)
	}

	return (
		<Popover
			placement='bottom'
			align={align}
			open={open}
			onOpenChange={setOpen}
			role='listbox'
			trapFocus={false}
			ariaLabel={label ?? placeholder}
			spacesClass='spaces-select-dropdown'
			containerClassName={containerClassName}
			matchTriggerWidth={matchTriggerWidth}
			trigger={
				<button
					type='button'
					onKeyDown={handleTriggerKeyDown}
					className={[
						'spaces-select spaces:inline-flex spaces:items-center spaces:gap-4 spaces:whitespace-nowrap spaces:font-default spaces:text-16/24',
						'spaces:px-16 spaces:py-8 spaces:rounded-12 spaces:bg-canvas-base',
						'spaces:hover:bg-canvas-4 spaces:cursor-pointer spaces:transition-colors',
						'spaces:focus-visible:outline-canvas-base spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2',
						spacesClass,
						className
					].join(' ')}
				>
					{label && <span className='spaces:text-content-3'>{label}</span>}
					<span
						className={`${selectedOption ? 'spaces:text-content-1' : 'spaces:text-content-3'}`}
					>
						{selectedOption?.label ?? placeholder}
					</span>
					{showCaret && (
						<Icon name='CaretDown' size={16} className='spaces:text-content-4' />
					)}
				</button>
			}
		>
			<div ref={listRef} onKeyDown={onKeyDown}>
				{options.map(opt => (
					<ListItem
						key={opt.value}
						label={opt.label}
						secondaryLabel={opt.secondaryLabel}
						isSelected={opt.value === value}
						onToggle={() => handleSelect(opt.value)}
						{...getItemProps(opt.value)}
					/>
				))}
			</div>
		</Popover>
	)
}
